import * as THREE from "three";
import type { MatLib } from "../world/materials";
import type { AudioSys } from "../engine/audio";
import type { Player } from "../player/player";
import type { TextureLibrary } from "../world/textures";

export type WeaponId = "ar" | "smg" | "shot" | "pistol";

export type WeaponDef = {
  id: WeaponId;
  name: string;
  cal: string;
  mode: string;
  dmg: number;
  rpm: number;
  mag: number;
  reserves: number;
  reload: number;
  pellets: number;
  spreadHip: number;
  spreadAds: number;
  range: number;
  recoilUp: number;
  recoilSide: number;
  adsTime: number;
  sound: string;
  auto: boolean;
};

export const WEAPONS: WeaponDef[] = [
  {
    id: "ar",
    name: "VS-56 KESTREL",
    cal: "5.56",
    mode: "AUTO",
    dmg: 28,
    rpm: 710,
    mag: 30,
    reserves: 210,
    reload: 1.85,
    pellets: 1,
    spreadHip: 0.018,
    spreadAds: 0.0035,
    range: 120,
    recoilUp: 0.028,
    recoilSide: 0.012,
    adsTime: 0.22,
    sound: "ar",
    auto: true,
  },
  {
    id: "smg",
    name: "SPINE-9",
    cal: "9mm",
    mode: "AUTO",
    dmg: 19,
    rpm: 920,
    mag: 32,
    reserves: 192,
    reload: 1.55,
    pellets: 1,
    spreadHip: 0.024,
    spreadAds: 0.006,
    range: 70,
    recoilUp: 0.02,
    recoilSide: 0.018,
    adsTime: 0.16,
    sound: "smg",
    auto: true,
  },
  {
    id: "shot",
    name: "MARROW 12",
    cal: "12G",
    mode: "PUMP",
    dmg: 16,
    rpm: 78,
    mag: 6,
    reserves: 30,
    reload: 2.6,
    pellets: 8,
    spreadHip: 0.055,
    spreadAds: 0.032,
    range: 28,
    recoilUp: 0.085,
    recoilSide: 0.03,
    adsTime: 0.28,
    sound: "shot",
    auto: false,
  },
  {
    id: "pistol",
    name: "KITE .45",
    cal: ".45",
    mode: "SEMI",
    dmg: 34,
    rpm: 280,
    mag: 10,
    reserves: 50,
    reload: 1.35,
    pellets: 1,
    spreadHip: 0.016,
    spreadAds: 0.004,
    range: 50,
    recoilUp: 0.034,
    recoilSide: 0.014,
    adsTime: 0.14,
    sound: "pistol",
    auto: false,
  },
];

export type WeaponState = {
  def: WeaponDef;
  mag: number;
  res: number;
  heat: number;
  nextShot: number;
  reloading: boolean;
  reloadT: number;
  ads: number;
  group: THREE.Group;
};

function gunPart(geo: THREE.BufferGeometry, mat: THREE.Material, x: number, y: number, z: number, sx = 1, sy = 1, sz = 1) {
  const m = new THREE.Mesh(geo, mat);
  m.position.set(x, y, z);
  m.scale.set(sx, sy, sz);
  m.castShadow = false;
  m.receiveShadow = true;
  m.frustumCulled = false;
  return m;
}

function buildViewmodel(id: WeaponId, mats: MatLib) {
  const g = new THREE.Group();
  const box = new THREE.BoxGeometry(1, 1, 1);
  const cyl = new THREE.CylinderGeometry(0.5, 0.5, 1, 12);
  cyl.rotateX(Math.PI / 2);
  const gm = mats.gunMetal;
  const gd = mats.gunDark;
  const ga = mats.gunAccent;
  const gl = mats.gunGlass ?? mats.emissiveLed ?? gd;
    const glove = gd;

  if (id === "ar" || id === "smg") {
    const long = id === "ar";
    const bl = long ? -0.62 : -0.48;
    // lower receiver + magwell
    g.add(gunPart(box, gd, 0.0, -0.03, 0.01, 0.042, 0.055, 0.16));
    // mag
    g.add(gunPart(box, gd, 0.0, -0.09, 0.02, 0.028, 0.09, 0.05));
    // pistol grip
    const grip = gunPart(box, gd, 0.0, -0.1, 0.08, 0.03, 0.1, 0.045);
    grip.rotation.x = 0.45;
    g.add(grip);
    // upper
    g.add(gunPart(box, gm, 0.0, 0.012, -0.06, 0.04, 0.04, long ? 0.28 : 0.2));
    // handguard
    g.add(gunPart(box, ga, 0.0, 0.0, long ? -0.28 : -0.22, 0.038, 0.038, long ? 0.2 : 0.14));
    // barrel
    g.add(gunPart(cyl, gm, 0.0, 0.006, bl + 0.08, 0.012, 0.012, long ? 0.28 : 0.18));
    // muzzle brake
    g.add(gunPart(box, gd, 0.0, 0.006, bl, 0.02, 0.016, 0.035));
    // top rail + optic
    g.add(gunPart(box, gd, 0.0, 0.036, -0.04, 0.018, 0.01, 0.18));
    // holo hood (open)
    g.add(gunPart(box, gd, -0.018, 0.058, -0.08, 0.004, 0.032, 0.04));
    g.add(gunPart(box, gd, 0.018, 0.058, -0.08, 0.004, 0.032, 0.04));
    g.add(gunPart(box, gd, 0.0, 0.074, -0.08, 0.04, 0.004, 0.04));
    g.add(gunPart(box, gd, 0.0, 0.042, -0.08, 0.04, 0.004, 0.04));
    g.add(gunPart(box, gl, 0.0, 0.058, -0.08, 0.03, 0.026, 0.006));
    // mittens
    g.add(gunPart(box, glove, 0.04, -0.09, 0.075, 0.05, 0.04, 0.07));
    g.add(gunPart(box, glove, -0.045, -0.02, long ? -0.26 : -0.2, 0.045, 0.032, 0.08));
    // stock
    g.add(gunPart(box, ga, 0.0, 0.0, 0.16, 0.03, 0.04, 0.12));
    g.add(gunPart(box, gd, 0.0, 0.01, 0.23, 0.038, 0.055, 0.04));
    // trigger guard
    g.add(gunPart(box, gm, 0.0, -0.055, 0.06, 0.02, 0.018, 0.04));
  } else if (id === "shot") {
    g.add(gunPart(box, gd, 0, -0.02, 0.02, 0.045, 0.05, 0.16));
    g.add(gunPart(cyl, gm, 0, 0.01, -0.22, 0.02, 0.02, 0.32));
    g.add(gunPart(cyl, ga, 0, -0.02, -0.16, 0.016, 0.016, 0.22));
    const grip = gunPart(box, gd, 0, -0.09, 0.07, 0.03, 0.1, 0.04);
    grip.rotation.x = 0.4;
    g.add(grip);
    g.add(gunPart(box, ga, 0, 0.0, 0.16, 0.035, 0.05, 0.14));
  } else {
    g.add(gunPart(box, gd, 0, -0.02, 0.02, 0.032, 0.045, 0.1));
    g.add(gunPart(cyl, gm, 0, 0.0, -0.1, 0.011, 0.011, 0.14));
    const grip = gunPart(box, gd, 0, -0.08, 0.05, 0.026, 0.09, 0.035);
    grip.rotation.x = 0.5;
    g.add(grip);
    g.add(gunPart(box, gm, 0, 0.02, 0.0, 0.03, 0.03, 0.06));
  }

  const muzzle = new THREE.Object3D();
  muzzle.position.set(0, 0.006, id === "shot" ? -0.4 : id === "pistol" ? -0.18 : id === "ar" ? -0.64 : -0.5);
  g.add(muzzle);
  g.userData.muzzle = muzzle;
  return g;
}

export class Arsenal {
  weapons: WeaponState[] = [];
  index = 0;
  group = new THREE.Group();
  private flash: THREE.Sprite;
  private smoke: THREE.Sprite;
  private flashT = 0;
  private sway = new THREE.Vector2();
  private drop = new THREE.Vector2();
  private wall = 0;

  constructor(mats: MatLib, tex: TextureLibrary, camera: THREE.Camera) {
    for (const def of WEAPONS) {
      const group = buildViewmodel(def.id, mats);
      group.visible = false;
      this.group.add(group);
      this.weapons.push({
        def,
        mag: def.mag,
        res: def.reserves,
        heat: 0,
        nextShot: 0,
        reloading: false,
        reloadT: 0,
        ads: 0,
        group,
      });
    }
    this.weapons[0].group.visible = true;
    camera.add(this.group);
    this.group.position.set(0.19, -0.175, -0.4);
    this.group.scale.setScalar(1.06);
    const key = new THREE.SpotLight(0xffd0a0, 0.55, 1.6, 0.7, 0.45);
    key.position.set(0.15, 0.2, 0.1);
    key.target.position.set(0, -0.05, -0.3);
    this.group.add(key);
    this.group.add(key.target);

    const flashMat = new THREE.SpriteMaterial({
      map: tex.muzzle,
      blending: THREE.AdditiveBlending,
      transparent: true,
      depthWrite: false,
      color: 0xffc070,
    });
    this.flash = new THREE.Sprite(flashMat);
    this.flash.scale.set(0.35, 0.35, 0.35);
    this.flash.visible = false;
    this.group.add(this.flash);

    const smokeMat = new THREE.SpriteMaterial({
      map: tex.smoke,
      transparent: true,
      depthWrite: false,
      opacity: 0.0,
      color: 0xb0b8c0,
    });
    this.smoke = new THREE.Sprite(smokeMat);
    this.smoke.scale.set(0.18, 0.18, 0.18);
    this.group.add(this.smoke);
  }

  get cur() {
    return this.weapons[this.index];
  }

  select(i: number) {
    if (i < 0 || i >= this.weapons.length) return;
    this.cur.group.visible = false;
    this.index = i;
    this.cur.group.visible = true;
    this.cur.reloading = false;
  }

  cycle(dir: number) {
    this.select((this.index + dir + this.weapons.length) % this.weapons.length);
  }

  tryReload(audio: AudioSys) {
    const w = this.cur;
    if (w.reloading || w.mag === w.def.mag || w.res <= 0) return;
    w.reloading = true;
    w.reloadT = w.def.reload;
    audio.play("reload", { vol: 0.55 });
  }

  update(
    dt: number,
    time: number,
    player: Player,
    firing: boolean,
    adsHeld: boolean,
    audio: AudioSys,
    onFire: (origin: THREE.Vector3, dir: THREE.Vector3, w: WeaponState) => void
  ) {
    const w = this.cur;
    w.ads = THREE.MathUtils.damp(w.ads, adsHeld && !w.reloading ? 1 : 0, 14, dt);
    player.ads = w.ads > 0.55;

    if (w.reloading) {
      w.reloadT -= dt;
      if (w.reloadT <= 0) {
        const need = w.def.mag - w.mag;
        const take = Math.min(need, w.res);
        w.mag += take;
        w.res -= take;
        w.reloading = false;
      }
    }

    const interval = 60 / w.def.rpm;
    const can = time >= w.nextShot && w.mag > 0 && !w.reloading && player.alive;
    const trigger = w.def.auto ? firing : firing && time >= w.nextShot;
    if (can && trigger && firing) {
      w.mag--;
      w.nextShot = time + interval;
      w.heat = Math.min(1, w.heat + 0.08);
      const up = w.def.recoilUp * (1 + w.heat * 0.6);
      const side = (Math.random() * 2 - 1) * w.def.recoilSide * (1 + w.heat);
      player.applyPunch(up, side);
      audio.play(w.def.sound, { vol: 0.72, rate: 0.96 + Math.random() * 0.08 });
      const origin = new THREE.Vector3();
      const dir = new THREE.Vector3();
      player.camera.getWorldPosition(origin);
      player.camera.getWorldDirection(dir);
      onFire(origin, dir, w);
      this.flashT = 0.045;
      const mz = w.group.userData.muzzle as THREE.Object3D;
      mz.getWorldPosition(this.flash.position);
      this.group.worldToLocal(this.flash.position);
      this.flash.position.copy(mz.position);
      this.flash.visible = true;
      this.flash.material.rotation = Math.random() * Math.PI;
      this.smoke.position.copy(mz.position);
      (this.smoke.material as THREE.SpriteMaterial).opacity = 0.45;
    }

    this.flashT -= dt;
    if (this.flashT <= 0) this.flash.visible = false;
    const sm = this.smoke.material as THREE.SpriteMaterial;
    sm.opacity = Math.max(0, sm.opacity - dt * 1.6);
    this.smoke.scale.setScalar(0.18 + (0.45 - sm.opacity) * 0.5);
    w.heat = Math.max(0, w.heat - dt * 0.55);

    // sway + bob + ads pose
    const moving = Math.hypot(player.mover.vel.x, player.mover.vel.z);
    this.sway.x = THREE.MathUtils.damp(this.sway.x, -player.mover.vel.x * 0.01, 8, dt);
    this.sway.y = THREE.MathUtils.damp(this.sway.y, -player.mover.vel.y * 0.01, 8, dt);
    const bob = Math.sin(player.bob) * 0.012 * Math.min(1, moving / 4);
    const bobY = Math.abs(Math.cos(player.bob)) * 0.01 * Math.min(1, moving / 4);
    const ads = w.ads;
    const hip = new THREE.Vector3(0.19, -0.175, -0.4);
    const aim = new THREE.Vector3(0.0, -0.062, -0.3);
    const reloadDrop = w.reloading ? Math.sin((1 - w.reloadT / w.def.reload) * Math.PI) * 0.18 : 0;
    this.group.position.lerpVectors(hip, aim, ads);
    this.group.position.x += this.sway.x + bob + this.wall * 0.12;
    this.group.position.y += this.sway.y - bobY - reloadDrop - this.wall * 0.08;
    this.group.rotation.set(-player.recoil.x * 0.9 - reloadDrop * 0.8, -player.recoil.y * 0.6, 0.04 * (1 - ads));
  }

  setWallPush(t: number) {
    this.wall = t;
  }
}
